import React, { useState, useEffect } from "react";
import { useAuth } from "../context/AuthContext";
import OneOmicsLogo from "./OneOmicsLogo";

export default function RegisterPage({ onGoLogin }) {
  const [form, setForm] = useState({
    username: "", password: "", confirmPassword: "",
    email: "", phone: "", institute: "", city: "", role: "viewer",
  });
  const [showPassword, setShowPassword]   = useState(false);
  const [error, setError]                 = useState("");
  const [success, setSuccess]             = useState(false);
  const [loading, setLoading]             = useState(false);
  const [mounted, setMounted]             = useState(false);
  const { register }                      = useAuth();

  useEffect(() => { setMounted(true); }, []);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    if (error) setError("");
  };

  const validate = () => {
    if (!form.username.trim())       return "Username is required.";
    if (form.username.length < 3)    return "Username must be at least 3 characters.";
    if (!form.email.trim())          return "Email is required.";
    if (!/\S+@\S+\.\S+/.test(form.email)) return "Enter a valid email address.";
    if (!form.phone.trim())          return "Phone number is required.";
    if (!/^\d{7,15}$/.test(form.phone.replace(/[\s\-+()]/g, "")))
                                     return "Enter a valid phone number.";
    if (!form.institute.trim())      return "Institute / Company is required.";
    if (!form.city.trim())           return "City is required.";
    if (!form.password)              return "Password is required.";
    if (form.password.length < 8)    return "Password must be at least 8 characters.";
    if (form.password !== form.confirmPassword) return "Passwords do not match.";
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const err = validate();
    if (err) { setError(err); return; }

    setLoading(true);
    setError("");
    try {
      const result = await register(form);
      if (result.success) {
        setSuccess(true);
      } else {
        setError(result.message || "Registration failed. Please try again.");
      }
    } catch {
      setError("Server error. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // ── Success screen ──────────────────────────────────────────────────────────
  if (success) return (
    <div className="login-root">
      <div className="grid-overlay" />
      <div className="glow-orb orb-1" />
      <div className="glow-orb orb-2" />
      <div className={`login-container mounted`} style={{ textAlign: "center" }}>
        <div style={{ fontSize: 48, marginBottom: 16 }}>⏳</div>
        <div style={{
          fontFamily: "'Barlow', sans-serif", fontSize: 13,
          letterSpacing: "0.14em", color: "#003087", marginBottom: 10,
        }}>REGISTRATION SUBMITTED</div>
        <h2 style={{
          fontFamily: "'Barlow', sans-serif", fontSize: 22,
          fontWeight: 800, color: "#1a2a3a", margin: "0 0 12px",
        }}>Pending Admin Approval</h2>
        <p style={{ color: "#5a7a9a", fontSize: 13, lineHeight: 1.7, marginBottom: 28 }}>
          Your account request has been received.<br />
          An administrator will review and approve your access shortly.
        </p>
        <button onClick={onGoLogin} style={{
          background: "none", border: "1px solid rgba(0,48,135,0.3)",
          color: "#003087", borderRadius: 8, padding: "10px 24px",
          fontFamily: "'Barlow', sans-serif", fontSize: 12,
          letterSpacing: "0.1em", cursor: "pointer",
        }}>
          ← Back to Login
        </button>
      </div>
      <Styles />
    </div>
  );

  // ── Fields config ───────────────────────────────────────────────────────────
  const fields = [
    { name: "username",        label: "USERNAME",          type: "text",     placeholder: "Choose a username",        icon: UserIcon },
    { name: "email",           label: "EMAIL",             type: "email",    placeholder: "your@email.com",           icon: MailIcon },
    { name: "phone",           label: "PHONE NUMBER",      type: "tel",      placeholder: "+91 98765 43210",          icon: PhoneIcon },
    { name: "institute",       label: "INSTITUTE / COMPANY", type: "text",   placeholder: "e.g. TNAU, OneOmics",      icon: BuildingIcon },
    { name: "city",            label: "CITY",              type: "text",     placeholder: "e.g. Chennai",             icon: CityIcon },
    { name: "password",        label: "PASSWORD",          type: showPassword ? "text" : "password",
      placeholder: "Min. 8 characters", icon: LockIcon, toggle: true },
    { name: "confirmPassword", label: "CONFIRM PASSWORD",  type: showPassword ? "text" : "password",
      placeholder: "Repeat your password", icon: LockIcon },
  ];

  return (
    <div className="login-root">
      <div className="grid-overlay" />
      <div className="glow-orb orb-1" />
      <div className="glow-orb orb-2" />

      <div className={`login-container reg-container ${mounted ? "mounted" : ""}`}>
        {/* Brand */}
        <div className="brand-header" style={{ marginBottom: 20 }}>
          <OneOmicsLogo height={40} maxWidth="190px" dark={false} />
        </div>

        <div className="divider-line" />

        <div className="login-title-block">
          <h1 className="login-title">Request Access</h1>
          <p className="login-subtitle">Fill in your details — admin will approve your account</p>
        </div>

        {error && (
          <div className="alert alert-error">
            <span className="alert-icon">⚠</span>
            <span>{error}</span>
          </div>
        )}

        <form onSubmit={handleSubmit} className="login-form" autoComplete="off">
          {/* Two-column grid for name + email */}
          <div className="field-row">
            {fields.slice(0, 2).map(f => (
              <FieldGroup key={f.name} f={f} form={form} onChange={handleChange}
                showPassword={showPassword} setShowPassword={setShowPassword} loading={loading} />
            ))}
          </div>

          {/* Phone + City side by side */}
          <div className="field-row">
            {fields.slice(2, 4).map(f => (
              <FieldGroup key={f.name} f={f} form={form} onChange={handleChange}
                showPassword={showPassword} setShowPassword={setShowPassword} loading={loading} />
            ))}
          </div>

          {/* City full width */}
          <FieldGroup f={fields[4]} form={form} onChange={handleChange}
            showPassword={showPassword} setShowPassword={setShowPassword} loading={loading} />



          {/* Passwords side by side */}
          <div className="field-row">
            {fields.slice(5, 7).map(f => (
              <FieldGroup key={f.name} f={f} form={form} onChange={handleChange}
                showPassword={showPassword} setShowPassword={setShowPassword} loading={loading} />
            ))}
          </div>

          <button type="submit" disabled={loading} className={`submit-btn ${loading ? "loading" : ""}`}>
            {loading ? (
              <span className="btn-loading"><span className="spinner" />SUBMITTING...</span>
            ) : (
              <><span>REQUEST ACCESS</span>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                  <path d="M5 12h14M12 5l7 7-7 7"/>
                </svg>
              </>
            )}
          </button>
        </form>

        <div className="login-footer">
          <p style={{ fontSize: 13, color: "#5a7a9a" }}>
            Already have an account?{" "}
            <button onClick={onGoLogin} style={{
              background: "none", border: "none", color: "#003087",
              cursor: "pointer", fontFamily: "'Barlow', sans-serif",
              fontSize: 13, textDecoration: "underline", textUnderlineOffset: 3,
            }}>Sign in</button>
          </p>
        </div>
      </div>

      <Styles />
    </div>
  );
}

// ── Sub-components ─────────────────────────────────────────────────────────────
function FieldGroup({ f, form, onChange, showPassword, setShowPassword, loading }) {
  return (
    <div className="field-group">
      <label className="field-label">{f.label}</label>
      <div className="field-wrapper">
        <span className="field-icon"><f.icon /></span>
        <input
          type={f.type}
          name={f.name}
          value={form[f.name]}
          onChange={onChange}
          placeholder={f.placeholder}
          disabled={loading}
          className="field-input"
          autoComplete="off"
        />
        {f.toggle && (
          <button type="button" className="toggle-pass"
            onClick={() => setShowPassword(p => !p)} tabIndex={-1}>
            {showPassword ? <EyeOffIcon /> : <EyeIcon />}
          </button>
        )}
      </div>
    </div>
  );
}

const UserIcon     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>;
const LockIcon     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><rect x="3" y="11" width="18" height="11" rx="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/></svg>;
const MailIcon     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/></svg>;
const PhoneIcon    = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07A19.5 19.5 0 0 1 4.69 12 19.79 19.79 0 0 1 1.61 3.18 2 2 0 0 1 3.6 1h3a2 2 0 0 1 2 1.72c.127.96.361 1.903.7 2.81a2 2 0 0 1-.45 2.11L7.91 8.64a16 16 0 0 0 6 6l.95-.95a2 2 0 0 1 2.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0 1 22 16.92z"/></svg>;
const BuildingIcon = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/><polyline points="9 22 9 12 15 12 15 22"/></svg>;
const CityIcon     = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/><circle cx="12" cy="9" r="2.5"/></svg>;
const EyeIcon      = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/></svg>;
const EyeOffIcon   = () => <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19"/><line x1="1" y1="1" x2="23" y2="23"/></svg>;

function Styles() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Roboto:wght@300;400;500;700;900&display=swap');
      *{box-sizing:border-box;margin:0;padding:0}
      .login-root{min-height:100vh;background:#f4f7fb;display:flex;align-items:center;justify-content:center;font-family:'Barlow', sans-serif;position:relative;overflow:hidden;padding:20px 0}
      .grid-overlay{display:none}
      .glow-orb{display:none}
      .login-container{width:100%;max-width:440px;background:#ffffff;border:1px solid rgba(0,0,0,0.08);border-radius:16px;padding:36px 32px;position:relative;z-index:10;box-shadow:0 4px 24px rgba(0,0,0,0.08);opacity:0;transform:translateY(24px);transition:opacity 0.6s ease,transform 0.6s ease}
      .reg-container{max-width:640px}
      .login-container.mounted{opacity:1;transform:translateY(0)}
      .brand-header{display:flex;align-items:center;gap:14px;margin-bottom:24px}
      .brand-icon{color:#003087;width:36px;flex-shrink:0}
      .brand-icon svg{width:100%;height:auto}
      .brand-name{font-family:'Barlow', sans-serif;font-size:15px;font-weight:700;letter-spacing:0.18em;color:#1a2a3a}
      .brand-sub{font-family:'Barlow', sans-serif;font-size:9px;letter-spacing:0.22em;color:#003087;margin-top:1px}
      .brand-text{display:flex;flex-direction:column}
      .divider-line{height:1px;background:linear-gradient(90deg,transparent,rgba(0,48,135,0.2),transparent);margin-bottom:24px}
      .login-title-block{margin-bottom:24px}
      .login-title{font-size:26px;font-weight:800;color:#1a2a3a;letter-spacing:-0.02em;line-height:1.1}
      .login-subtitle{font-size:12px;color:#5a7a9a;margin-top:5px;letter-spacing:0.02em}
      .alert{display:flex;align-items:flex-start;gap:10px;padding:12px 14px;border-radius:10px;margin-bottom:16px;font-size:13px;animation:alertIn 0.3s ease}
      @keyframes alertIn{from{opacity:0;transform:translateY(-6px)}to{opacity:1;transform:translateY(0)}}
      .alert-error{background:rgba(239,68,68,0.07);border:1px solid rgba(239,68,68,0.2);color:#dc2626}
      .alert-icon{font-size:15px;margin-top:1px}
      .login-form{display:flex;flex-direction:column;gap:16px}
      .field-row{display:grid;grid-template-columns:1fr 1fr;gap:14px}
      .field-group{display:flex;flex-direction:column;gap:7px}
      .field-label{font-family:'Barlow', sans-serif;font-size:10px;font-weight:500;letter-spacing:0.12em;color:#5a7a9a}
      .field-wrapper{position:relative;display:flex;align-items:center}
      .field-icon{position:absolute;left:13px;color:#a0b4c8;display:flex;align-items:center;pointer-events:none;transition:color 0.2s}
      .field-wrapper:focus-within .field-icon{color:#003087}
      .field-input{width:100%;background:#f8fafc;border:1px solid rgba(0,0,0,0.12);border-radius:10px;padding:12px 13px 12px 42px;color:#1a2a3a;font-family:'Barlow', sans-serif;font-size:13px;outline:none;transition:all 0.2s}
      .field-input::placeholder{color:#a0b4c8}
      .field-input:focus{border-color:#003087;background:#fff;box-shadow:0 0 0 3px rgba(0,48,135,0.08)}
      .field-input:disabled{opacity:0.5;cursor:not-allowed}
      select.field-input{color:#1a2a3a;background:#f8fafc;cursor:pointer}
      select.field-input option{background:#fff;color:#1a2a3a}
      .toggle-pass{position:absolute;right:12px;background:none;border:none;color:#a0b4c8;cursor:pointer;padding:4px;display:flex;align-items:center;transition:color 0.2s}
      .toggle-pass:hover{color:#5a7a9a}
      .submit-btn{display:flex;align-items:center;justify-content:center;gap:10px;width:100%;padding:13px;background:#003087;color:#fff;border:none;border-radius:10px;font-family:'Barlow', sans-serif;font-size:13px;font-weight:700;letter-spacing:0.06em;cursor:pointer;transition:all 0.2s;margin-top:4px;box-shadow:0 4px 16px rgba(0,48,135,0.2)}
      .submit-btn:hover:not(:disabled){background:#002070;box-shadow:0 6px 20px rgba(0,48,135,0.3);transform:translateY(-1px)}
      .submit-btn:disabled{opacity:0.5;cursor:not-allowed;box-shadow:none}
      .btn-loading{display:flex;align-items:center;gap:10px}
      .spinner{width:14px;height:14px;border:2px solid rgba(255,255,255,0.3);border-top-color:#fff;border-radius:50%;animation:spin 0.7s linear infinite}
      @keyframes spin{to{transform:rotate(360deg)}}
      .login-footer{margin-top:20px;display:flex;flex-direction:column;align-items:center;gap:8px}
      @media(max-width:600px){.field-row{grid-template-columns:1fr}.reg-container{max-width:100%;margin:16px;padding:24px 18px}.login-title{font-size:22px}}
    `}</style>
  );
}